import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { SlidersHorizontal, X, ChevronDown, Check } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";

const TYPES = ["Angle", "Convertible", "Lit"];
const COLORS = [
  { name: "Beige", hex: "#d8c7b5" }, { name: "Gris", hex: "#9a9a9a" },
  { name: "Noir", hex: "#1f1f1f" }, { name: "Blanc", hex: "#efe9df" },
  { name: "Marine", hex: "#2a3950" }, { name: "Vert", hex: "#2f4030" },
  { name: "Graphite", hex: "#454545" }, { name: "Marron", hex: "#6b4a38" },
  { name: "Moutarde", hex: "#c9932e" }, { name: "Turquoise", hex: "#2fa39c" },
  { name: "Jaune", hex: "#d9c04a" }, { name: "Rouge", hex: "#8c2f2f" },
];
const SIZES = ["Angle gauche", "Angle droit", "Standard", "140×200", "160×200", "180×200"];
const SORTS = [
  { v: "newest", l: "Nouveautés" }, { v: "bestsellers", l: "Meilleures ventes" },
  { v: "price_asc", l: "Prix croissant" }, { v: "price_desc", l: "Prix décroissant" },
  { v: "rating", l: "Mieux notés" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileFilters, setMobileFilters] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);

  const category = params.get("category") || "";
  const search = params.get("search") || "";
  const sort = params.get("sort") || "newest";
  const promo = params.get("promo") === "1";
  const inStock = params.get("in_stock") === "1";
  const maxPrice = Number(params.get("max_price") || 600);
  const selTypes = (params.get("types") || "").split(",").filter(Boolean);
  const selColors = (params.get("colors") || "").split(",").filter(Boolean);
  const selSizes = (params.get("sizes") || "").split(",").filter(Boolean);
  const minRating = Number(params.get("min_rating") || 0);

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (category) p.category = category;
    if (search) p.search = search;
    if (sort) p.sort = sort;
    if (promo) p.promo = true;
    if (inStock) p.in_stock = true;
    if (maxPrice < 600) p.max_price = maxPrice;
    if (selTypes.length) p.types = selTypes.join(",");
    if (selColors.length) p.colors = selColors.join(",");
    if (selSizes.length) p.sizes = selSizes.join(",");
    if (minRating) p.min_rating = minRating;
    api.get("/products", { params: p }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === "" || value == null || value === false) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const toggleInList = (key, current, value) => {
    const set = new Set(current);
    set.has(value) ? set.delete(value) : set.add(value);
    update(key, [...set].join(","));
  };

  const clearAll = () => setParams(new URLSearchParams(), { replace: true });

  const activeCount = useMemo(
    () => [...params.keys()].filter((k) => !["sort", "search"].includes(k)).length,
    [params]
  );

  const currentSort = SORTS.find((s) => s.v === sort) || SORTS[0];

  const FilterPanel = () => (
    <div className="space-y-8" data-testid="filter-panel">
      <div>
        <h3 className="mb-3 font-accent text-xs uppercase tracking-widest text-gold-dark">Catégorie</h3>
        <div className="space-y-1.5">
          <button onClick={() => update("category", "")} className={`block text-sm transition-colors ${!category ? "font-semibold text-foreground" : "text-muted-foreground hover:text-foreground"}`}>Toutes</button>
          {categories.map((c) => (
            <button key={c.id} data-testid={`filter-cat-${c.id}`} onClick={() => update("category", c.id)} className={`block text-sm transition-colors ${category === c.id ? "font-semibold text-gold-dark" : "text-muted-foreground hover:text-foreground"}`}>{c.name}</button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="mb-3 font-accent text-xs uppercase tracking-widest text-gold-dark">Prix maximum</h3>
        <input
          type="range" min="150" max="600" step="10" value={maxPrice}
          data-testid="filter-price-slider"
          onChange={(e) => update("max_price", e.target.value)}
          className="w-full accent-gold"
        />
        <p className="mt-1 text-sm text-muted-foreground">Jusqu'à <span className="font-semibold text-foreground">{maxPrice} €</span></p>
      </div>

      <div>
        <h3 className="mb-3 font-accent text-xs uppercase tracking-widest text-gold-dark">Type</h3>
        <div className="flex flex-wrap gap-2">
          {TYPES.map((t) => (
            <button key={t} data-testid={`filter-type-${t.replace(/\s/g, "-")}`} onClick={() => toggleInList("types", selTypes, t)}
              className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${selTypes.includes(t) ? "border-gold bg-gold text-obsidian" : "border-border text-muted-foreground hover:border-gold"}`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="mb-3 font-accent text-xs uppercase tracking-widest text-gold-dark">Taille</h3>
        <div className="flex flex-wrap gap-2">
          {SIZES.map((s) => (
            <button key={s} onClick={() => toggleInList("sizes", selSizes, s)}
              className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${selSizes.includes(s) ? "border-gold bg-gold text-obsidian" : "border-border text-muted-foreground hover:border-gold"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="mb-3 font-accent text-xs uppercase tracking-widest text-gold-dark">Note minimum</h3>
        <div className="flex gap-2">
          {[0, 4, 4.5, 4.8].map((r) => (
            <button key={r} onClick={() => update("min_rating", r || "")}
              className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${minRating === r ? "border-gold bg-gold text-obsidian" : "border-border text-muted-foreground hover:border-gold"}`}>
              {r === 0 ? "Toutes" : `${r}+`}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <label className="flex cursor-pointer items-center gap-3 text-sm" data-testid="filter-instock">
          <input type="checkbox" checked={inStock} onChange={(e) => update("in_stock", e.target.checked ? "1" : "")} className="h-4 w-4 accent-gold" />
          Disponible immédiatement
        </label>
      </div>

      {activeCount > 0 && (
        <button onClick={clearAll} data-testid="clear-filters" className="w-full rounded-full border border-border py-2.5 text-sm text-muted-foreground transition-colors hover:border-destructive hover:text-destructive">
          Réinitialiser les filtres
        </button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background pt-8">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Reveal className="mb-8">
          <p className="mb-2 font-accent text-xs uppercase tracking-[0.25em] text-gold-dark">Collection</p>
          <h1 className="font-serif text-4xl font-medium lg:text-5xl">
            {search ? `Recherche : « ${search} »` : category ? categories.find((c) => c.id === category)?.name || "Nos canapés" : promo ? "Nos promotions" : "Tous nos canapés"}
          </h1>
        </Reveal>

        <div className="mb-8 flex items-center justify-between gap-4">
          <button onClick={() => setMobileFilters(true)} data-testid="mobile-filter-button" className="flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm lg:hidden">
            <SlidersHorizontal size={16} /> Filtres {activeCount > 0 && `(${activeCount})`}
          </button>
          <p className="hidden text-sm text-muted-foreground lg:block">{products.length} produit{products.length > 1 ? "s" : ""}</p>
          <div className="relative">
            <button onClick={() => setSortOpen((o) => !o)} data-testid="sort-button" className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm">
              Trier : <span className="font-semibold">{currentSort.l}</span>
              <ChevronDown size={15} className={`transition-transform ${sortOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {sortOpen && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                  className="absolute right-0 z-30 mt-2 w-48 overflow-hidden rounded-2xl bg-card p-1.5 shadow-luxe" data-testid="sort-menu">
                  {SORTS.map((s) => (
                    <button key={s.v} data-testid={`sort-${s.v}`} onClick={() => { update("sort", s.v); setSortOpen(false); }}
                      className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition-colors hover:bg-secondary ${sort === s.v ? "font-semibold text-gold-dark" : ""}`}>
                      {s.l} {sort === s.v && <Check size={14} />}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="grid gap-10 lg:grid-cols-[240px_1fr]">
          <aside className="hidden lg:block">
            <div className="sticky top-28"><FilterPanel /></div>
          </aside>

          <div>
            {loading ? (
              <div className="grid grid-cols-2 gap-5 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[3/4] animate-pulse rounded-3xl bg-secondary" />)}
              </div>
            ) : products.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-border py-24 text-center" data-testid="no-products">
                <p className="font-serif text-2xl">Aucun produit trouvé</p>
                <p className="mt-2 text-sm text-muted-foreground">Essayez de modifier vos filtres.</p>
                <button onClick={clearAll} className="mt-6 rounded-full bg-obsidian px-6 py-3 text-sm font-semibold text-cream">Réinitialiser</button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-5 lg:grid-cols-3" data-testid="products-grid">
                {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile filters */}
      <AnimatePresence>
        {mobileFilters && (
          <>
            <motion.div className="fixed inset-0 z-[80] bg-obsidian/50 backdrop-blur-sm lg:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setMobileFilters(false)} />
            <motion.div className="fixed bottom-0 left-0 z-[90] max-h-[85vh] w-full overflow-y-auto rounded-t-3xl bg-cream p-6 lg:hidden" initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }}>
              <div className="mb-6 flex items-center justify-between">
                <h2 className="font-serif text-2xl">Filtres</h2>
                <button onClick={() => setMobileFilters(false)}><X size={22} /></button>
              </div>
              <FilterPanel />
              <button onClick={() => setMobileFilters(false)} className="mt-8 w-full rounded-full bg-obsidian py-3.5 text-sm font-semibold text-cream">Voir les résultats ({products.length})</button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
